import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Save, RotateCcw, Info } from "lucide-react";
import { toast } from "sonner";

const TIP_SECTIONS = [
  {
    heading: "Leadership",
    rows: [
      ["head_counselor_full", "head_counselor_half", "Head Counselor"],
    ],
  },
  {
    heading: "Assistant Counselors — by category",
    rows: [
      ["asst_adult_full", "asst_adult_half", "Adult"],
      ["asst_college_full", "asst_college_half", "College"],
      ["asst_hs_full", "asst_hs_half", "High School"],
    ],
  },
  {
    heading: "CIT",
    rows: [
      ["cit_supervisor_full", "cit_supervisor_half", "CIT Supervisor (CIT letter only)"],
      ["cit_full", "cit_half", "CIT (Counselor-In-Training)"],
    ],
  },
  {
    heading: "AM/PM Care",
    rows: [
      ["ampm_full", "ampm_half", "AM/PM Counselor"],
    ],
  },
  {
    heading: "Transportation",
    rows: [
      ["bus_driver_full", "bus_driver_half", "Bus Driver"],
      ["bus_counselor_full", "bus_counselor_half", "Bus Counselor"],
    ],
  },
];

export default function AdminPage() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm(structuredClone(settings));
  }, [settings]);

  if (!form) return <div className="p-10 text-[#13315C]/60">Loading…</div>;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setTip = (k, v) =>
    setForm({ ...form, tip_amounts: { ...form.tip_amounts, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        camp_name: form.camp_name,
        camp_est: form.camp_est,
        season_year: Number(form.season_year),
        phone: form.phone,
        signature_1: form.signature_1,
        signature_2: form.signature_2,
        signoff: form.signoff,
        last_day_of_camp: form.last_day_of_camp,
        half_season_1_deadline: form.half_season_1_deadline,
        full_season_deadline: form.full_season_deadline,
        tip_amounts: form.tip_amounts,
      };
      await api.patch("/settings", payload);
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const reseed = async () => {
    if (!window.confirm("Wipe & re-seed all groups, buses, and settings from 2026 defaults?")) return;
    try {
      await api.post("/seed", null, { params: { force: true } });
      toast.success("Data re-seeded from 2026 defaults");
      qc.invalidateQueries();
    } catch (e) {
      toast.error("Reseed failed");
    }
  };

  return (
    <div data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
            Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B2545]">
            Camp Settings
          </h1>
          <p className="text-[#13315C]/75 mt-2 max-w-2xl text-sm sm:text-base">
            Edit camp identity, <b>season year</b>, deadlines, and suggested
            tip amounts. Change the year here each summer and every letter
            updates instantly.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="admin-reseed"
            onClick={reseed}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#0B2545] border border-[#0B2545] font-semibold hover:bg-[#0B2545] hover:text-[#FDF6EC]"
          >
            <RotateCcw className="w-4 h-4" /> Reset roster
          </button>
          <button
            data-testid="admin-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0B2545] text-[#FDF6EC] font-semibold hover:bg-[#13315C] disabled:opacity-70"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Camp identity */}
        <section className="paper-card p-6 lg:col-span-2">
          <SectionTitle title="Camp Identity" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Camp name" value={form.camp_name} onChange={(v) => set("camp_name", v)} testid="admin-camp_name" />
            <Field label="Established" value={form.camp_est} onChange={(v) => set("camp_est", v)} testid="admin-camp_est" />
            <Field label="Season Year" value={form.season_year} onChange={(v) => set("season_year", v)} testid="admin-season_year" />
            <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="admin-phone" />
            <Field label="Sign-off (e.g., Anchors Aweigh,)" value={form.signoff} onChange={(v) => set("signoff", v)} testid="admin-signoff" />
            <Field label="Signature line 1" value={form.signature_1} onChange={(v) => set("signature_1", v)} testid="admin-signature_1" />
            <Field label="Signature line 2" value={form.signature_2} onChange={(v) => set("signature_2", v)} testid="admin-signature_2" />
          </div>
        </section>

        {/* Deadlines */}
        <section className="paper-card p-6">
          <SectionTitle title="Deadlines" />
          <div className="space-y-4">
            <Field label="Last day of camp" value={form.last_day_of_camp} onChange={(v) => set("last_day_of_camp", v)} testid="admin-last_day" />
            <Field label="Half Season 1 deadline" value={form.half_season_1_deadline} onChange={(v) => set("half_season_1_deadline", v)} testid="admin-half1" />
            <Field label="Full Season deadline" value={form.full_season_deadline} onChange={(v) => set("full_season_deadline", v)} testid="admin-full" />
          </div>
          <div className="mt-5 flex items-start gap-2 text-xs text-[#13315C]/70 p-3 rounded-lg bg-[#EAF3F7]">
            <Info className="w-4 h-4 flex-none mt-0.5" />
            <div>Use full sentence format like &ldquo;Thursday, August 20&rdquo;. Text is used verbatim in every letter.</div>
          </div>
        </section>

        {/* Tip amounts */}
        <section className="paper-card p-6 lg:col-span-3">
          <SectionTitle title="Suggested Tip Amounts" />
          <p className="text-sm text-[#13315C]/70 -mt-2 mb-5">
            Set separate rates per grade. Only the rates for grades that
            actually appear in each group will be shown on that group&apos;s
            letter.
          </p>

          {TIP_SECTIONS.map((section) => (
            <div key={section.heading} className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/70 uppercase">
                  {section.heading}
                </div>
                <div className="flex-1 h-px bg-[#E9DDC8]" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 px-2 pb-2 text-[11px] font-mono-camp uppercase tracking-widest text-[#13315C]/60">
                <div className="sm:col-span-5">Role</div>
                <div className="sm:col-span-3">Full Season</div>
                <div className="sm:col-span-3">Half Season</div>
              </div>
              <div className="space-y-3 sm:space-y-2">
                {section.rows.map(([fullKey, halfKey, label]) => (
                  <div key={fullKey} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-center paper-tint sm:paper-tint-none sm:bg-transparent sm:border-0 p-3 sm:p-0">
                    <div className="col-span-2 sm:col-span-5 text-sm text-[#0B2545] font-semibold sm:pl-2">
                      {label}
                    </div>
                    <label className="col-span-1 sm:col-span-3 block">
                      <span className="sm:hidden text-[9px] font-mono-camp uppercase tracking-widest text-[#13315C]/60">Full Season</span>
                      <input
                        data-testid={`admin-tip-${fullKey}`}
                        value={form.tip_amounts?.[fullKey] || ""}
                        onChange={(e) => setTip(fullKey, e.target.value)}
                        placeholder="$–"
                        className="w-full px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                      />
                    </label>
                    <label className="col-span-1 sm:col-span-3 block">
                      <span className="sm:hidden text-[9px] font-mono-camp uppercase tracking-widest text-[#13315C]/60">Half Season</span>
                      <input
                        data-testid={`admin-tip-${halfKey}`}
                        value={form.tip_amounts?.[halfKey] || ""}
                        onChange={(e) => setTip(halfKey, e.target.value)}
                        placeholder="$–"
                        className="w-full px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                      />
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

function SectionTitle({ title }) {
  return (
    <div className="mb-5">
      <h3 className="font-display text-2xl font-bold text-[#0B2545]">{title}</h3>
      <div className="mt-2 wave-line">~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~</div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-[#13315C]/60 font-mono-camp">
        {label}
      </span>
      <input
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
      />
    </label>
  );
}
